/**
 * ngPrometZoneReport — shared, pure render core of the zone view »Promet po
 * conah« (#711; Priloga 1 §3.3). The sidebar panel AND the `/ng-report` page
 * build their result from the same `GET …/promet/zones` answer through these
 * functions, so the two can never drift. Data → HTML string or ECharts option;
 * no DOM, no fetch, no chart instance.
 *
 * Every figure is the server's (#709). This module only rounds (through
 * ngPrometZoneFormat) and words it.
 */

import type { EChartsOption } from 'echarts';
import type {
  PrometAreaFigures,
  PrometPeriodKey,
  PrometPointFigures,
  PrometZoneSeries,
  PrometZonesResponse,
} from '../../../../../../shared/types/ngPromet.js';
import { formatLjubljanaTs } from '../../../../../../shared/utils/ljubljanaTime.js';
import { appliedFilters, filterLine, isAllWeekdays } from '../../../../../../shared/utils/ngPrometFilters.js';
import {
  ljubljanaAxisLabel,
  ljubljanaAxisPointerLabel,
  ljubljanaTooltipFormatter,
} from '../ngChartTime.js';
import { EXPORT_ROW_CAP_CLIENT } from './ngPrometReport.js';
import {
  NO_DATA_LABEL,
  NO_VALUE,
  directionLetters,
  fmtCount,
  fmtSharePct,
  fmtSignedCount,
  fmtSignedPct,
} from '../NgPrometPanel/ngPrometZoneFormat.js';

export const ZONE_COPY = {
  pTitle: 'Obdobje za primerjavo (P)',
  rTitle: 'Referenčno obdobje (R)',
  diffTitle: 'Razlika P − R',
  lastAvailable: 'zadnji razpoložljivi podatek',
  deltaAbs: 'Δ abs.',
  deltaPct: 'Δ %',
  total: 'Skupaj',
  minMax: 'najmanj – največ',
  daysInAverage: 'Dni v povprečju',
  hoursInAverage: 'Ur v povprečju',
  areaEmpty: 'V izbranih obdobjih ni podatkov za to območje.',
  // The two block headings in MONG's words (#863); the exports keep their own sheet and column names.
  pointsTitle: 'Porazdelitev prometa po vstopnih točkah',
  pointsCaption: 'Delež vstopne točke v prometu območja, obdobji P in R',
  pointsColName: 'Točka',
  pointTitle: 'Podatki za vstopno točko',
  pointHintMapFirst: 'Izberi na zemljevidu ali v seznamu zgoraj.',
  pointHintListFirst: 'Izberi v seznamu zgoraj ali na zemljevidu.',
  pointColFigure: 'Podatek',
  leftOut: 'Brez podatkov',
  chartTitle: 'Časovna vrsta',
  chartEmpty: 'Ni točk za prikaz v izbranem obdobju.',
  chartClamped: 'Časovna vrsta je skrajšana na zadnji del obdobja.',
  noComparison: 'Za primerjavo ni razpoložljivih podatkov.',
};

function esc(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function row(label: string, value: string): string {
  return `<div class="ngz-row"><dt>${label}</dt><dd>${value}</dd></div>`;
}

/** »vstopi − izstopi« for a zone, »S→J − J→S« for the line. */
function deltaWords(area: PrometAreaFigures): string {
  if (area.kind === 'line') {
    const l = directionLetters('line');
    return `${l.in} − ${l.out}`;
  }
  return esc(`${area.in_label.toLowerCase()} − ${area.out_label.toLowerCase()}`);
}

/** The row label of a point's share: Priloga wording for a zone. */
function shareWords(area: PrometAreaFigures): { in: string; out: string } {
  const l = directionLetters(area.kind);
  return area.kind === 'line'
    ? { in: `% prometa ${l.in}`, out: `% prometa ${l.out}` }
    : { in: `% vstopov v cono (${l.in})`, out: `% izstopov iz cone (${l.out})` };
}

type NoDataReasons = PrometPointFigures['no_data_reason'];

function periodWords(periods: PrometPeriodKey[]): string {
  const p = periods.includes('p');
  const r = periods.includes('r');
  if (p && r) return 'v obdobjih P in R';
  return p ? 'v obdobju P' : 'v obdobju R';
}

/** »premalo podatkov v referenčnem obdobju« (#727) — mirrored in NgPrometZoneExportService. */
function lowCoverageWords(periods: PrometPeriodKey[]): string {
  if (periods.length > 1) return 'premalo podatkov v obdobjih P in R';
  return periods[0] === 'p' ? 'premalo podatkov v obdobju za primerjavo' : 'premalo podatkov v referenčnem obdobju';
}

function splitReasons(
  periods: PrometPeriodKey[],
  reasons: NoDataReasons,
): { none: PrometPeriodKey[]; low: PrometPeriodKey[] } {
  return {
    none: periods.filter((k) => reasons[k] !== 'low_coverage'),
    low: periods.filter((k) => reasons[k] === 'low_coverage'),
  };
}

/** Why a point is left out, without the label: »v obdobju P«, »premalo podatkov v referenčnem obdobju«. */
function noDataWords(periods: PrometPeriodKey[], reasons: NoDataReasons): string {
  const { none, low } = splitReasons(periods, reasons);
  const parts = [none.length > 0 ? periodWords(none) : '', low.length > 0 ? lowCoverageWords(low) : ''];
  return parts.filter(Boolean).join(', ');
}

/** »brez podatkov v obdobju P«, »brez podatkov (premalo podatkov v referenčnem obdobju)«. */
function noDataText(periods: PrometPeriodKey[], reasons: NoDataReasons): string {
  const { none, low } = splitReasons(periods, reasons);
  const head = none.length > 0 ? `${NO_DATA_LABEL} ${periodWords(none)}` : NO_DATA_LABEL;
  return low.length > 0 ? `${head} (${lowCoverageWords(low)})` : head;
}

function withPct(abs: number, pct: number | null): string {
  return `${fmtSignedCount(abs)} <span class="ngz-sub">(${fmtSignedPct(pct)})</span>`;
}

// ----------------------------------------------------------------------------
// Result HTML
// ----------------------------------------------------------------------------

export interface ZoneResultOptions {
  /** Export / new-tab row, placed under the three blocks. */
  actionsHtml?: string;
  /** Panel: the points table has a button per row. Report page: plain text. */
  selectable: boolean;
  /** Which way of choosing a point the hint names first (phone: the list). */
  listFirst?: boolean;
  /**
   * P is »the last available« (the form's P was empty). The panel says so
   * itself: its follow-up requests carry the resolved P, so the answer's own
   * `defaulted` flag is true only on the first of them. Default: the answer's flag.
   */
  lastAvailable?: boolean;
}

export function buildZoneResultHtml(response: PrometZonesResponse, opts: ZoneResultOptions): string {
  const area = response.areas.find((a) => a.key === response.area);
  if (!area) return `<div class="ngz-result"><p class="ngz-empty">${ZONE_COPY.areaEmpty}</p></div>`;

  const chartBlock = `<section class="ngz-block">
      <h4>${ZONE_COPY.chartTitle}</h4>
      ${response.series.clamped ? `<p class="ngz-note">${ZONE_COPY.chartClamped}</p>` : ''}
      <div class="ngz-chart" data-role="zone-chart"></div>
      <div class="ngz-chart-empty" data-role="zone-chart-empty" hidden>${ZONE_COPY.chartEmpty}</div>
    </section>`;

  const notes = response.notes_sl.length
    ? `<ul class="ngz-notes">${response.notes_sl.map((n) => `<li>${esc(n)}</li>`).join('')}</ul>`
    : '';

  // A category or weekday filter is named right under the title (#740); no filter, no line.
  const applied = appliedFilters(response.applied);
  const filters = filterLine(applied.categories, applied.weekdays);
  const filtersHtml = filters ? `<p class="ngz-filter-line" data-role="zone-filter-line">${esc(filters)}</p>` : '';

  return `<div class="ngz-result">
      <h3 class="ngz-area-title">${esc(area.name)}</h3>
      ${filtersHtml}
      ${buildAreaBlocksHtml(response, area, opts.lastAvailable ?? response.comparison?.defaulted ?? false)}
      ${opts.actionsHtml ?? ''}
      ${buildPointsTableHtml(area, response.access_point_id, opts.selectable)}
      ${buildPointBlockHtml(response, area, opts, averagedLabel(response))}
      ${buildLeftOutHtml(area)}
      ${chartBlock}
      ${notes}
    </div>`;
}

/** What R's average is taken over: days, or in the hour view the instants of that hour. */
function averagedLabel(response: PrometZonesResponse): string {
  return response.interval === 'hour' ? ZONE_COPY.hoursInAverage : ZONE_COPY.daysInAverage;
}

function buildAreaBlocksHtml(
  response: PrometZonesResponse,
  area: PrometAreaFigures,
  lastAvailable: boolean,
): string {
  const cmp = response.comparison;
  const pPeriod = cmp
    ? `${esc(cmp.label_sl)}${lastAvailable ? ` <span class="ngz-tag">${ZONE_COPY.lastAvailable}</span>` : ''}`
    : ZONE_COPY.noComparison;
  const rPeriod = esc(response.reference.label_sl);
  const inLabel = esc(area.in_label);
  const outLabel = esc(area.out_label);
  const words = deltaWords(area);
  const averaged = averagedLabel(response);
  // A P of several days says over how many it averages — also when a weekday
  // filter leaves only one of them (#863); a one-day or one-hour P does not.
  const pSeveralDays = (!!cmp && cmp.first_day !== cmp.last_day) || (area.p?.days ?? 0) > 1;

  if (!area.p || !area.r || !area.diff) {
    return `<div class="ngz-blocks">
        <section class="ngz-block ngz-block-p"><h4>${ZONE_COPY.pTitle}</h4><p class="ngz-period">${pPeriod}</p></section>
        <section class="ngz-block ngz-block-r"><h4>${ZONE_COPY.rTitle}</h4><p class="ngz-period">${rPeriod}</p></section>
      </div>
      <p class="ngz-empty">${ZONE_COPY.areaEmpty}</p>`;
  }
  const { p, r, diff } = area;

  const pBlock = `<section class="ngz-block ngz-block-p">
      <h4>${ZONE_COPY.pTitle}</h4>
      <p class="ngz-period">${pPeriod}</p>
      <dl class="ngz-figures">
        ${row(inLabel, fmtCount(p.in_count))}
        ${row(outLabel, fmtCount(p.out_count))}
        ${row(`${ZONE_COPY.deltaAbs} ${words}`, fmtSignedCount(p.net))}
        ${row(`${ZONE_COPY.deltaPct} ${words}`, fmtSignedPct(p.net_pct))}
        ${pSeveralDays ? row(ZONE_COPY.daysInAverage, fmtCount(p.days)) : ''}
      </dl>
    </section>`;

  const rBlock = `<section class="ngz-block ngz-block-r">
      <h4>${ZONE_COPY.rTitle}</h4>
      <p class="ngz-period">${rPeriod}</p>
      <dl class="ngz-figures">
        ${row(inLabel, fmtCount(r.in_count))}
        ${row(outLabel, fmtCount(r.out_count))}
        ${row(`${ZONE_COPY.deltaAbs} ${words}`, fmtSignedCount(r.net))}
        ${row(`${ZONE_COPY.deltaPct} ${words}`, fmtSignedPct(r.net_pct))}
        ${row(`${inLabel}, ${ZONE_COPY.minMax}`, `${fmtCount(r.in_min)} – ${fmtCount(r.in_max)}`)}
        ${row(`${outLabel}, ${ZONE_COPY.minMax}`, `${fmtCount(r.out_min)} – ${fmtCount(r.out_max)}`)}
        ${row(averaged, fmtCount(r.days))}
      </dl>
    </section>`;

  const diffBlock = `<section class="ngz-block ngz-block-diff">
      <h4>${ZONE_COPY.diffTitle}</h4>
      <dl class="ngz-figures">
        ${row(inLabel, withPct(diff.in_abs, diff.in_pct))}
        ${row(outLabel, withPct(diff.out_abs, diff.out_pct))}
        ${row(ZONE_COPY.total, withPct(diff.total_abs, diff.total_pct))}
        ${row(`${ZONE_COPY.deltaAbs} ${words}`, fmtSignedCount(diff.net_abs))}
      </dl>
    </section>`;

  return `<div class="ngz-blocks">${pBlock}${rBlock}${diffBlock}</div>`;
}

/** Every access point of the area with its shares — the map labels as a table. */
function buildPointsTableHtml(area: PrometAreaFigures, chosenId: number | null, selectable: boolean): string {
  const l = directionLetters(area.kind);
  const head = `<tr>
      <th scope="col">${ZONE_COPY.pointsColName}</th>
      <th scope="col" class="ngz-num">${l.in}: P</th>
      <th scope="col" class="ngz-num">${l.in}: R</th>
      <th scope="col" class="ngz-num">${l.out}: P</th>
      <th scope="col" class="ngz-num">${l.out}: R</th>
    </tr>`;
  const body = area.points
    .map((pt) => {
      const chosen = pt.access_point_id === chosenId;
      const name = selectable
        ? `<button type="button" class="ngz-point-btn" data-role="zone-point" data-point-id="${pt.access_point_id}" aria-pressed="${chosen}">${esc(pt.name)}</button>`
        : esc(pt.name);
      const cls = [chosen ? 'ngz-row-chosen' : '', pt.counted ? '' : 'ngz-row-nodata'].filter(Boolean).join(' ');
      const cells =
        pt.counted && pt.p && pt.r
          ? `<td class="ngz-num">${fmtSharePct(pt.p.in_share_pct)}</td>
             <td class="ngz-num">${fmtSharePct(pt.r.in_share_pct)}</td>
             <td class="ngz-num">${fmtSharePct(pt.p.out_share_pct)}</td>
             <td class="ngz-num">${fmtSharePct(pt.r.out_share_pct)}</td>`
          : `<td class="ngz-nodata" colspan="4">${noDataText(pt.no_data, pt.no_data_reason)}</td>`;
      return `<tr${cls ? ` class="${cls}"` : ''}><th scope="row">${name}</th>${cells}</tr>`;
    })
    .join('');
  return `<section class="ngz-block">
      <h4>${ZONE_COPY.pointsTitle}</h4>
      <div class="ngz-table-wrap"><table class="ngz-table">
        <caption class="ngz-sr">${ZONE_COPY.pointsCaption}</caption>
        <thead>${head}</thead><tbody>${body}</tbody>
      </table></div>
    </section>`;
}

/** The chosen point's own figures, P beside R (Priloga: »Vstopna točka (P)« / »(R)«). */
function buildPointBlockHtml(
  response: PrometZonesResponse,
  area: PrometAreaFigures,
  opts: ZoneResultOptions,
  averaged: string,
): string {
  const pt = area.points.find((x) => x.access_point_id === response.access_point_id);
  if (!pt) {
    if (!opts.selectable) return '';
    const hint = opts.listFirst ? ZONE_COPY.pointHintListFirst : ZONE_COPY.pointHintMapFirst;
    return `<section class="ngz-block" data-role="zone-point-block">
        <h4>${ZONE_COPY.pointTitle}</h4>
        <p class="ngz-hint">${hint}</p>
      </section>`;
  }
  return `<section class="ngz-block" data-role="zone-point-block">
      <h4>${ZONE_COPY.pointTitle}</h4>
      <p class="ngz-point-name">${esc(pt.name)}</p>
      ${pt.counted ? '' : `<p class="ngz-nodata">${noDataText(pt.no_data, pt.no_data_reason)}</p>`}
      ${buildPointFiguresHtml(area, pt, averaged)}
    </section>`;
}

function buildPointFiguresHtml(area: PrometAreaFigures, pt: PrometPointFigures, averaged: string): string {
  if (!pt.p && !pt.r) return '';
  const words = deltaWords(area);
  const shares = shareWords(area);
  const { p, r, diff } = pt;
  const line = (label: string, pv: string, rv: string, dv: string): string =>
    `<tr><th scope="row">${label}</th><td class="ngz-num">${pv}</td><td class="ngz-num">${rv}</td><td class="ngz-num">${dv}</td></tr>`;
  const cnt = (v: number | undefined): string => (v === undefined ? NO_VALUE : fmtCount(v));
  const body = [
    line(esc(area.in_label), cnt(p?.in_count), cnt(r?.in_count), diff ? withPct(diff.in_abs, diff.in_pct) : NO_VALUE),
    line(shares.in, fmtSharePct(p?.in_share_pct), fmtSharePct(r?.in_share_pct), ''),
    line(esc(area.out_label), cnt(p?.out_count), cnt(r?.out_count), diff ? withPct(diff.out_abs, diff.out_pct) : NO_VALUE),
    line(shares.out, fmtSharePct(p?.out_share_pct), fmtSharePct(r?.out_share_pct), ''),
    line(`${ZONE_COPY.deltaAbs} ${words}`, fmtSignedCount(p?.net), fmtSignedCount(r?.net), diff ? fmtSignedCount(diff.net_abs) : NO_VALUE),
    line(`${ZONE_COPY.deltaPct} ${words}`, fmtSignedPct(p?.net_pct), fmtSignedPct(r?.net_pct), ''),
    // The point's own days (#863): the shared days when it counts, its own when it is left out.
    line(averaged, cnt(p?.days), cnt(r?.days), ''),
  ].join('');
  return `<div class="ngz-table-wrap"><table class="ngz-table ngz-point-table">
      <thead><tr>
        <th scope="col">${ZONE_COPY.pointColFigure}</th>
        <th scope="col" class="ngz-num">P</th>
        <th scope="col" class="ngz-num">R</th>
        <th scope="col" class="ngz-num">P − R</th>
      </tr></thead>
      <tbody>${body}</tbody>
    </table></div>`;
}

function buildLeftOutHtml(area: PrometAreaFigures): string {
  if (area.left_out.length === 0) return '';
  const items = area.left_out.map((x) => `${esc(x.name)} (${noDataWords(x.no_data, x.no_data_reason)})`).join('; ');
  return `<p class="ngz-left-out" data-role="zone-left-out"><strong>${ZONE_COPY.leftOut}:</strong> ${items}. Te točke niso vštete v vsote območja.</p>`;
}

// ----------------------------------------------------------------------------
// Export size — mirror of the server's estimateZonesExportRows
// (src/server/services/NgPrometZoneExportService.ts); keep the two in step.
// The CSV holds the figures and the series. The Excel file also holds the
// hourly source rows of every point of the area over the series window, so a
// daily view over several years can pass the cap although its chart is short.
// ----------------------------------------------------------------------------

export function estimateZoneExportRows(response: PrometZonesResponse, ext: 'csv' | 'xlsx'): number {
  const figureRows = response.areas.reduce((n, a) => n + 4 + a.points.length * 4, 0);
  const seriesRows = response.series.points.length * (response.access_point_id !== null ? 2 : 1);
  if (ext === 'csv') return figureRows + seriesRows;
  const spanMs = Date.parse(response.series.to) - Date.parse(response.series.from);
  const hours = Number.isFinite(spanMs) && spanMs > 0 ? Math.ceil(spanMs / 3_600_000) : 0;
  const points = response.areas.find((a) => a.key === response.area)?.points.length ?? 0;
  return Math.max(figureRows, seriesRows, hours * points);
}

/** Why an export of this answer would be refused, or '' when it fits. */
export function zoneExportBlock(response: PrometZonesResponse, ext: 'csv' | 'xlsx'): string {
  const rows = estimateZoneExportRows(response, ext);
  if (rows <= EXPORT_ROW_CAP_CLIENT) return '';
  return (
    `Obdobje je predolgo za izvoz v ${ext === 'xlsx' ? 'Excel' : 'CSV'} ` +
    `(${rows.toLocaleString('sl-SI')} vrstic, največ ${EXPORT_ROW_CAP_CLIENT.toLocaleString('sl-SI')}). ` +
    `Skrajšajte obdobji${ext === 'xlsx' ? ' ali izvozite CSV' : ''}.`
  );
}

// ----------------------------------------------------------------------------
// Chart
// ----------------------------------------------------------------------------

export function zoneSeriesHasData(series: PrometZoneSeries): boolean {
  return series.points.some(
    (p) => p.in_count !== null || p.out_count !== null || p.point_in !== null || p.point_out !== null,
  );
}

const R_BAND = 'rgba(250, 204, 21, 0.20)';
const P_BAND = 'rgba(220, 38, 38, 0.16)';
const P_LINE = '#dc2626';

/**
 * The area's two directions over the span of R and P (the server builds the
 * span), the chosen point's two directions when one is chosen, R shaded, P
 * shaded and marked with a line (a one-hour P is too thin to see as a band),
 * and zoom by wheel / pinch and by the slider.
 *
 * Under a weekday filter (#740) the series is a calendar grid with a gap on
 * every excluded day, so a value can stand alone between two gaps — and a line
 * draws nothing for a lone value. Then: no sampler (LTTB throws lone values
 * away), point symbols on a day series, and on an hour series a point on P's
 * own bucket (a P of one hour is kept on an excluded day).
 */
export function buildZoneChartOption(response: PrometZonesResponse): EChartsOption {
  const area = response.areas.find((a) => a.key === response.area);
  const inName = area?.in_label ?? 'Vstopi';
  const outName = area?.out_label ?? 'Izstopi';
  const point = area?.points.find((p) => p.access_point_id === response.access_point_id) ?? null;
  const pts = response.series.points;
  const granularity = response.series.bucket === 'day' ? 'date' : 'datetime';

  const bands: unknown[] = [
    [
      { name: 'R', xAxis: response.reference.from, itemStyle: { color: R_BAND } },
      { xAxis: response.reference.to },
    ],
  ];
  const cmp = response.comparison;
  if (cmp) {
    bands.push([{ name: 'P', xAxis: cmp.from, itemStyle: { color: P_BAND } }, { xAxis: cmp.to }]);
  }

  const weekdayFiltered = !isAllWeekdays(appliedFilters(response.applied).weekdays);
  const daySymbols = weekdayFiltered && response.series.bucket === 'day';
  const base = {
    type: 'line',
    showSymbol: daySymbols,
    ...(daySymbols ? { symbolSize: 5 } : {}),
    connectNulls: false,
    ...(weekdayFiltered ? {} : { sampling: 'lttb' }),
  };
  // P's own bucket of an hour series, marked with a point when it has a value.
  const pBucket = weekdayFiltered && response.series.bucket === 'hour' && cmp ? pts.find((p) => p.ts === cmp.from) : undefined;
  const pMark = (value: number | null | undefined): Record<string, unknown> =>
    pBucket && typeof value === 'number'
      ? { markPoint: { silent: true, symbol: 'circle', symbolSize: 7, label: { show: false }, data: [{ coord: [pBucket.ts, value] }] } }
      : {};
  const series: Record<string, unknown>[] = [
    {
      ...base,
      name: inName,
      data: pts.map((p) => [p.ts, p.in_count]),
      ...pMark(pBucket?.in_count),
      // Bands and the P line ride on the first series so they paint once.
      markArea: { silent: true, label: { position: 'insideTop', fontSize: 10, color: '#475569' }, data: bands },
      ...(cmp
        ? {
            markLine: {
              silent: true,
              symbol: 'none',
              lineStyle: { color: P_LINE, type: 'dashed', width: 1.5 },
              // The band above already carries the letter »P«.
              label: { show: false },
              data: [{ xAxis: cmp.from }],
            },
          }
        : {}),
    },
    { ...base, name: outName, data: pts.map((p) => [p.ts, p.out_count]), ...pMark(pBucket?.out_count) },
  ];
  if (point) {
    series.push(
      {
        ...base,
        name: `${point.name} – ${inName.toLowerCase()}`,
        lineStyle: { type: 'dashed' },
        data: pts.map((p) => [p.ts, p.point_in]),
        ...pMark(pBucket?.point_in),
      },
      {
        ...base,
        name: `${point.name} – ${outName.toLowerCase()}`,
        lineStyle: { type: 'dashed' },
        data: pts.map((p) => [p.ts, p.point_out]),
        ...pMark(pBucket?.point_out),
      },
    );
  }

  return {
    animation: false,
    grid: { left: 48, right: 18, top: 34, bottom: 58 },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'cross', label: { formatter: ljubljanaAxisPointerLabel() } },
      formatter: ljubljanaTooltipFormatter(granularity, (v) => fmtCount(v)),
    },
    legend: { type: 'scroll', top: 0, textStyle: { fontSize: 10 } },
    // Day labels on the axis for both intervals (the tooltip carries the hour);
    // a label that would run into its neighbour is left out.
    xAxis: { type: 'time', axisLabel: { fontSize: 10, hideOverlap: true, formatter: ljubljanaAxisLabel('date') } },
    yAxis: { type: 'value', axisLabel: { fontSize: 10 } },
    dataZoom: [
      { type: 'inside', xAxisIndex: 0, filterMode: 'none' },
      {
        type: 'slider',
        xAxisIndex: 0,
        filterMode: 'none',
        height: 18,
        bottom: 8,
        labelFormatter: (value: number) => formatLjubljanaTs(new Date(value).toISOString(), 'date'),
      },
    ],
    series,
  } as EChartsOption;
}
